import {
  type AbroadHome,
  type LessonItem,
  type SchoolEvent,
  type StudentGrade,
  addDays,
  canJoinLesson,
  clubsOnDay,
  lessonFit,
  timeOf,
  todayIso,
  useBackpack,
  useClubMemberships,
  useClubs,
  useEvents,
  useLessons,
  weekStart,
} from '@shkola/api';
import { subjectLook } from '@shkola/design';
import { Link } from '@tanstack/react-router';
import { type KeyboardEvent, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowUpRightIcon,
  BackpackIcon,
  CalendarDotsIcon,
  CaretLeftIcon,
  CaretRightIcon,
  type FilledIcon,
  SunHorizonIcon,
  XIcon,
} from '../../design/icons';
import { useAge } from '../../design/ui';
import { gradeShort } from '../../lib/labels';
import { useMinute } from '../../lib/now';
import { LessonCard, fullName, useLessonCard, useLessonGrades } from './Day';
import { EventArt, SubjectArt } from './LineArt';
import { EVENT_LOOK, type Week, forStudent, useWeek } from './GlassDay';
import {
  type Slot,
  type StudentContext,
  capitalize,
  dayLong,
  dayNow,
  isoWeekday,
  roomOf,
  metaOf,
  minutesBetween,
  monthName,
  plain,
  slotsOf,
  weekdayShort,
} from './model';
import {
  DARK,
  FLAT,
  GRAD,
  type Grad,
  INK,
  INK_FAINT,
  INK_SOFT,
  LINE,
  PAPER,
  SUBJECT_GRAD,
  SUBJECT_SHAPE,
  Scribble,
  Shape,
  grad,
} from './ShapesArt';

/*
 * The lessons of «Форми»: the days of the week on top as soft squares — done ones scribbled over in
 * pen on lime, today in orange, the chosen one ringed; the lessons as flat cards in their subject's
 * colour with its shape, the times on the right; a break is a short line of words; «now» is a line
 * with a lime time chip on it (ref. the schedule with times on the axis). After the lessons — the
 * club; for the next day — what to pack. A click on a lesson opens its card.
 */

/** The title of a block: big and light, a small count lifted over it, a note, the buttons. */
export function Head({
  title,
  count,
  note,
  children,
}: {
  title: string;
  count?: number;
  note?: string;
  children?: ReactNode;
}) {
  const age = useAge();
  return (
    <header className="mb-5 flex min-h-10 items-start gap-3">
      <h2
        className={`min-w-0 font-display font-light tracking-[-0.035em] ${age === 'junior' ? 'text-[34px] leading-[38px]' : 'text-[30px] leading-[36px]'}`}
        style={{ color: INK }}
      >
        {title}
        {count !== undefined && count > 0 && (
          <sup
            className="ml-1 font-sans text-[14px] font-medium tracking-normal"
            style={{ color: INK_SOFT }}
          >
            {count}
          </sup>
        )}
      </h2>
      {note && (
        <span className="truncate pt-2.5 text-[14px]" style={{ color: INK_FAINT }}>
          {note}
        </span>
      )}
      {children && <span className="ml-auto flex shrink-0 items-center gap-1.5">{children}</span>}
    </header>
  );
}

/** A round outlined button (‹ ›). */
export const roundButton =
  'inline-flex size-9 shrink-0 items-center justify-center rounded-full bg-white ring-1 ring-[#E4E0D7] ring-inset transition-colors hover:bg-[#F5F3EF]';

/**
 * «Форми 2» (his «стрелочки в кружочке — просто outline, тёмно-фиолетовым»): a round icon button is an
 * outline of the dark, empty inside, the icon in it; it fills on hover.
 */
export const ringButton =
  'inline-flex size-9 shrink-0 items-center justify-center rounded-full text-[var(--sh-dark)] ring-[1.5px] ring-[var(--sh-dark)] ring-inset transition-colors duration-200 hover:bg-[var(--sh-dark)] hover:text-white';

/** The same, a notch smaller (inside a popover). */
export const ringSmall = ringButton.replace('size-9', 'size-8');

/**
 * «Форми 2»: a button is a pill with its words and the icon in a ringed circle at the end (ref. «Info ⊖
 * Work ⊕»); dark — the main one, light — white with a hairline. Anything without that circle is a label.
 */
export const pillClass = (kind: 'dark' | 'light', size: 'md' | 'sm' = 'md') =>
  `inline-flex shrink-0 items-center rounded-full font-medium whitespace-nowrap transition-colors duration-200 ${
    size === 'sm' ? 'h-9 gap-2 pr-1 pl-3.5 text-[13px]' : 'h-11 gap-2.5 pr-1.5 pl-4.5 text-[14px]'
  } ${
    kind === 'dark'
      ? 'bg-[var(--sh-dark)] text-white hover:bg-[#2f2357]'
      : 'bg-white text-[var(--sh-ink)] ring-1 ring-[#E4E0D7] ring-inset hover:bg-[#FAF9F6]'
  }`;

/** The ringed icon at the end of a pill button. */
export function PillIcon({ icon: Icon, size = 'md' }: { icon: FilledIcon; size?: 'md' | 'sm' }) {
  return (
    <span
      className={`flex shrink-0 items-center justify-center rounded-full ring-[1.5px] ring-current ring-inset ${size === 'sm' ? 'size-7' : 'size-8'}`}
    >
      <Icon size={size === 'sm' ? 12 : 14} weight="bold" aria-hidden />
    </span>
  );
}

/** The ↗ into a section: a pine circle (ref. the round arrows of «Creative Leaders Circle»). */
export function OpenArrow({
  to,
  label,
  soft = false,
}: {
  to: '/schedule' | '/homework' | '/grades' | '/events';
  label: string;
  /** «Форми 2»: an outline, not a fill */
  soft?: boolean;
}) {
  return (
    <Link
      to={to}
      aria-label={label}
      title={label}
      className={
        soft
          ? ringButton
          : 'inline-flex size-9 shrink-0 items-center justify-center rounded-full text-white transition-transform duration-500 ease-spring hover:rotate-45'
      }
      style={soft ? undefined : { backgroundColor: DARK }}
    >
      <ArrowUpRightIcon size={16} weight="bold" aria-hidden />
    </Link>
  );
}

/**
 * A small mark on a card. «Форми»: a pill, white or in a colour. «Форми 2»: a label is a soft rectangle,
 * never a pill — the pills there are buttons.
 */
export function Chip({
  children,
  tone,
  soft = false,
}: {
  children: ReactNode;
  tone?: 'pine' | 'orange';
  soft?: boolean;
}) {
  if (soft)
    return (
      <span
        className="inline-flex h-6 shrink-0 items-center gap-1 rounded-[7px] px-2 text-[12px] leading-none font-medium whitespace-nowrap"
        style={{
          backgroundColor: tone === 'pine' ? DARK : 'rgb(255 255 255 / 0.75)',
          color: tone === 'pine' ? '#FFFFFF' : tone === 'orange' ? '#D9471A' : INK,
        }}
      >
        {children}
      </span>
    );
  const ground = tone === 'pine' ? DARK : tone === 'orange' ? FLAT.orange : '#FFFFFF';
  return (
    <span
      className="inline-flex h-6 shrink-0 items-center gap-1 rounded-full px-2.5 text-[12px] leading-none font-medium whitespace-nowrap"
      style={{ backgroundColor: ground, color: tone ? '#FFFFFF' : INK }}
    >
      {children}
    </span>
  );
}

/** Closes a popover on a click outside it and on Escape. */
export function useDismiss(open: boolean, close: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) close();
    };
    const onKey = (e: globalThis.KeyboardEvent) => e.key === 'Escape' && close();
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);
  return ref;
}

/** The chosen day on the dark: its own colour with the light of the cards' corner. */
const DARK_GROUND = `radial-gradient(90% 70% at 0% 0%, rgb(255 255 255 / 0.16) 0%, rgb(255 255 255 / 0) 60%), linear-gradient(165deg, ${DARK} 0%, ${DARK} 100%)`;

/** Today's date when another day is chosen. */
const TODAY_INK = '#E8551F';

/**
 * How a day looks in «Форми 2» (his «не делать текущий день с такой чёрной обводкой», 28.10): the
 * chosen day filled — the dark, or orange when it is today; today not chosen — its date in orange.
 */
function softDay(on: boolean, isToday: boolean, done: boolean) {
  if (on && isToday) return { backgroundImage: grad('orange'), color: '#FFFFFF' };
  if (on) return { backgroundImage: DARK_GROUND, color: '#FFFFFF' };
  // a day gone by is only paler (his «убрать» of the scribble over it, 29.10)
  if (done) return { backgroundColor: PAPER, color: INK, opacity: 0.55 };
  return { backgroundColor: PAPER, color: isToday ? TODAY_INK : INK };
}

function Days({ week, soft }: { week: Week; soft: boolean }) {
  const { t } = useTranslation();
  return (
    <div
      className="grid gap-2"
      style={{ gridTemplateColumns: `repeat(${week.days.length}, minmax(0, 1fr))` }}
    >
      {week.days.map(({ date, lessons, events }) => {
        const on = date === week.selected;
        const isToday = date === week.today;
        const done = date < week.today && lessons.length > 0;
        const test = lessons.some((l) => l.isTest && l.status !== 'cancelled');
        const event = events.find((e) => e.kind !== 'holiday');
        const off = lessons.length === 0;
        if (soft) {
          return (
            <button
              key={date}
              type="button"
              aria-pressed={on}
              onClick={() => week.select(date)}
              title={isToday ? t('studentHome.day.today') : undefined}
              className="relative flex h-[68px] min-w-0 flex-col items-center justify-center overflow-hidden rounded-[18px] transition-transform duration-300 ease-spring hover:-translate-y-0.5"
              style={softDay(on, isToday, done)}
            >
              <span
                className="text-[12px] leading-4 font-medium"
                style={{ opacity: on || isToday ? 0.85 : off ? 0.4 : 0.6 }}
              >
                {weekdayShort(date)}
              </span>
              <span
                className="font-display text-[26px] leading-8 font-light tracking-[-0.03em]"
                style={{ opacity: off && !on && !isToday ? 0.4 : 1 }}
              >
                {Number(date.slice(8))}
              </span>
              {test && (
                <Shape
                  kind="burst"
                  size={11}
                  color={on ? '#FFFFFF' : FLAT.orange}
                  className="absolute top-2 right-2"
                />
              )}
              {event && (
                <span
                  aria-hidden
                  className="absolute bottom-1.5 size-1.5 rounded-full"
                  style={{ backgroundColor: on ? '#FFFFFF' : FLAT[EVENT_FLAT[event.kind]] }}
                />
              )}
            </button>
          );
        }
        return (
          <button
            key={date}
            type="button"
            aria-pressed={on}
            onClick={() => week.select(date)}
            title={isToday ? t('studentHome.day.today') : undefined}
            className="relative flex h-[68px] min-w-0 flex-col items-center justify-center overflow-hidden rounded-[18px] transition-[box-shadow,transform] duration-300 ease-spring hover:-translate-y-0.5"
            style={{
              backgroundColor: isToday ? FLAT.orange : done ? FLAT.lime : PAPER,
              color: isToday ? '#FFFFFF' : INK,
              boxShadow: on ? `inset 0 0 0 2px ${INK}` : undefined,
            }}
          >
            <span
              className="text-[12px] leading-4 font-medium"
              style={{ opacity: isToday ? 0.85 : off ? 0.4 : 0.6 }}
            >
              {weekdayShort(date)}
            </span>
            <span
              className="font-display text-[26px] leading-8 font-light tracking-[-0.03em]"
              style={{ opacity: off && !isToday ? 0.4 : 1 }}
            >
              {Number(date.slice(8))}
            </span>
            {done && (
              <Scribble className="absolute inset-1 h-[calc(100%-8px)] w-[calc(100%-8px)]" />
            )}
            {test && (
              <Shape
                kind="burst"
                size={11}
                color={isToday ? '#FFFFFF' : FLAT.orange}
                className="absolute top-2 right-2"
              />
            )}
            {event && (
              <span
                aria-hidden
                className="absolute bottom-1.5 size-1.5 rounded-full"
                style={{
                  backgroundColor: isToday ? '#FFFFFF' : FLAT[EVENT_FLAT[event.kind]],
                }}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}

/** «Форми 2»: an event's gradient by its kind. */
export const EVENT_GRAD: Record<SchoolEvent['kind'], Grad> = {
  holiday: 'mint',
  celebration: 'pink',
  meeting: 'butter',
  trip: 'sky',
  exam: 'lavender',
  other: 'aqua',
};

/** The colour of an event by its kind (the timeline cards of «У школі» take the same). */
export const EVENT_FLAT: Record<SchoolEvent['kind'], keyof typeof FLAT> = {
  holiday: 'mint',
  celebration: 'pink',
  meeting: 'butter',
  trip: 'cobalt',
  exam: 'lavender',
  other: 'aqua',
};

const onKeys = (open: () => void) => (e: KeyboardEvent) => {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    open();
  }
};

function LessonRow({
  lesson,
  grades,
  state,
  now,
  isToday,
  soft,
  home,
  onOpen,
}: {
  lesson: LessonItem;
  /** «Форми 2»: the subject's gradient and its picture, the name in the colour's ink */
  soft: boolean;
  grades: StudentGrade[];
  state: 'done' | 'live' | 'next' | 'ahead';
  now: number;
  isToday: boolean;
  /** a student abroad: the times by the family's clock, the Kyiv time under, which lessons are not live */
  home?: AbroadHome | null;
  onOpen: (lesson: LessonItem, el: HTMLElement) => void;
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const tone = subjectLook(lesson.subject).tone;
  const look = SUBJECT_SHAPE[tone];
  const softLook = SUBJECT_GRAD[tone];
  const cancelled = lesson.status === 'cancelled';
  const start = Date.parse(lesson.startsAt);
  const end = Date.parse(lesson.endsAt);
  const join = isToday && state !== 'done' && canJoinLesson(lesson, todayIso()) && end > now;
  const open = () => ref.current && onOpen(lesson, ref.current);
  return (
    <div
      ref={ref}
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={onKeys(open)}
      className={`relative isolate flex min-h-[60px] w-full cursor-pointer items-center gap-3 overflow-hidden rounded-[20px] py-2.5 pr-4 pl-3 text-left outline-none transition-[filter] hover:brightness-[0.98] focus-visible:ring-2 focus-visible:ring-[#16372D]/40 ${cancelled || state === 'done' ? 'opacity-50' : ''}`}
      style={{
        backgroundColor: cancelled ? FLAT.stone : FLAT[look.fill],
        backgroundImage: soft ? grad(cancelled ? 'stone' : softLook) : undefined,
        boxShadow: state === 'live' ? `inset 0 0 0 2px ${INK}` : undefined,
        paddingLeft: soft ? 16 : undefined,
      }}
    >
      {soft ? (
        // his «иконки как в неделе — крупно, тихо, фоном» (28.10): the subject's picture behind the row
        <SubjectArt
          icon={subjectLook(lesson.subject).icon}
          tone={cancelled ? 'stone' : softLook}
          size={86}
          ghost
          className="pointer-events-none absolute top-1/2 right-[68px] -z-10 -translate-y-[42%]"
        />
      ) : (
        <Shape kind={look.shape} size={30} color={FLAT[look.mark]} />
      )}
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1">
          <span
            className={`truncate text-[16px] leading-[22px] font-medium ${cancelled ? 'line-through' : ''}`}
            style={{ color: soft && !cancelled ? GRAD[softLook].ink : INK }}
          >
            {lesson.subject}
          </span>
          {cancelled ? (
            <Chip soft={soft}>{t('studentHome.cancelled')}</Chip>
          ) : (
            <>
              {lesson.isTest && (
                <Chip soft={soft} tone="orange">
                  {t('studentHome.test')}
                </Chip>
              )}
              {lesson.substitute && <Chip soft={soft}>{t('studentHome.substitute')}</Chip>}
              {lesson.format === 'online' && <Chip soft={soft}>{t('studentHome.online')}</Chip>}
              {lesson.format === 'shelter' && <Chip soft={soft}>{t('studentHome.shelter')}</Chip>}
            </>
          )}
          {grades.map((g) => (
            <Chip key={g.id} soft={soft}>
              {gradeShort(t, g.value, g.special)}
            </Chip>
          ))}
          {state === 'live' && (
            <Chip tone="pine" soft={soft}>
              {t('studentHome.glass.week.left', {
                min: Math.max(1, Math.ceil((end - now) / 60_000)),
              })}
            </Chip>
          )}
          {state === 'next' && isToday && (
            <Chip soft={soft}>
              {t('studentHome.glass.week.in', { min: Math.max(1, minutesBetween(now, start)) })}
            </Chip>
          )}
        </span>
        <span className="block truncate text-[12.5px] leading-[18px]" style={{ color: INK_SOFT }}>
          {[
            home &&
              lessonFit(lesson, home) !== 'live' &&
              t(`abroad.fit.${lessonFit(lesson, home)}`),
            metaOf(t, lesson),
          ]
            .filter(Boolean)
            .join(' · ')}
        </span>
      </span>
      {join && (
        <Link
          to="/lesson/$lessonId"
          params={{ lessonId: lesson.id }}
          onClick={(e) => e.stopPropagation()}
          className="inline-flex h-9 shrink-0 items-center rounded-full px-4 text-[13px] font-medium text-white"
          style={{ backgroundColor: DARK }}
        >
          {t('student.lessons.join')}
        </Link>
      )}
      <span className="flex shrink-0 flex-col items-end leading-tight" style={{ color: INK }}>
        <span className="font-display text-[17px] font-light tracking-[-0.02em]">
          {timeOf(lesson.startsAt, home?.timezone)}
        </span>
        <span className="text-[12px] whitespace-nowrap" style={{ color: INK_SOFT }}>
          {home ? t('abroad.kyiv', { time: timeOf(lesson.startsAt) }) : timeOf(lesson.endsAt)}
        </span>
      </span>
      {state === 'live' && (
        <span className="absolute inset-x-0 bottom-0 h-1 bg-white/60">
          <span
            className="block h-full"
            style={{
              width: `${Math.round(((now - start) / (end - start)) * 100)}%`,
              backgroundColor: INK,
            }}
          />
        </span>
      )}
    </div>
  );
}

/** «Now» across the day: the time in a lime chip on a line, what it is now. */
function Now({ time, soft, children }: { time: string; soft: boolean; children: string }) {
  return (
    <div className="flex h-9 items-center gap-2" aria-live="polite">
      <span
        className={`inline-flex h-6 items-center px-2.5 text-[12px] font-semibold ${soft ? 'rounded-[7px]' : 'rounded-full'}`}
        style={{ backgroundColor: FLAT.lime, color: INK }}
      >
        {time}
      </span>
      <span className="h-[1.5px] flex-1 rounded-full" style={{ backgroundColor: INK }} />
      <span className="shrink-0 text-[12.5px] font-medium" style={{ color: INK }}>
        {children}
      </span>
    </div>
  );
}

/** A break not going on: a short line of words. */
function Gap({ slot }: { slot: Extract<Slot, { kind: 'break' }> }) {
  const { t } = useTranslation();
  const label =
    slot.minutes >= 40
      ? t('studentHome.day.window', { min: slot.minutes })
      : slot.minutes >= 20
        ? t('studentHome.day.bigBreak', { min: slot.minutes })
        : t('studentHome.day.break', { min: slot.minutes });
  return (
    <div className="flex h-6 items-center gap-2 pl-[54px] text-[12px]" style={{ color: INK_FAINT }}>
      <span className="h-px w-3" style={{ backgroundColor: INK_FAINT }} />
      {label}
    </div>
  );
}

/** What to pack for the next school day, as one butter card. */
function Pack({ ctx, date, soft }: { ctx: StudentContext; date: string; soft: boolean }) {
  const { t } = useTranslation();
  const pack = useBackpack(ctx.schoolId, ctx.child.memberId);
  if (pack.date !== date || pack.items.length === 0) return null;
  return (
    <div
      className="mt-2 flex items-start gap-3 rounded-[20px] px-3.5 py-3"
      style={{
        backgroundColor: FLAT.butter,
        backgroundImage: soft ? grad('butter') : undefined,
        color: INK,
      }}
    >
      <BackpackIcon size={20} weight="fill" aria-hidden className="mt-0.5 shrink-0" />
      <p className="min-w-0 text-[14px] leading-5">
        <span className="font-medium">{t('studentHome.glass.week.pack')}:</span>{' '}
        {pack.items.map((i) => plain(i.label)).join(', ')}
      </p>
    </div>
  );
}

/** The first of the month `n` months from the one of `iso`. */
const monthFrom = (iso: string, n: number) => {
  const d = new Date(`${iso.slice(0, 7)}-01T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() + n, 1);
  return d.toISOString().slice(0, 10);
};

/**
 * The month to pick any day from (his «вместо стрелочек — кнопку календаря, чтобы окошко с календарем»,
 * then on the page «Розклад» — «сразу месячный календарь, как в Google и Apple», 28.10): the chosen day
 * and today as on the week, a dot for a test and for an event, the holidays tinted mint. `ahead` adds
 * under it what is coming this month — the tests and the events — each a tap to its day.
 */
export function MonthGrid({
  ctx,
  week,
  onPick,
  ahead = false,
  big = false,
}: {
  ctx: StudentContext;
  week: Week;
  onPick?: () => void;
  ahead?: boolean;
  /** on the page: bigger days */
  big?: boolean;
}) {
  const { t } = useTranslation();
  const [month, setMonth] = useState(() => monthFrom(week.selected, 0));
  const first = weekStart(month);
  const last = addDays(weekStart(addDays(monthFrom(month, 1), -1)), 6);
  const studentId = ctx.child.memberId;
  const scope = useMemo(() => ({ kind: 'student' as const, studentId }), [studentId]);
  const lessons = useLessons(ctx.schoolId, scope, first, last);
  const events = useEvents(ctx.schoolId, first);
  const all = (events.data ?? []).filter(forStudent(ctx));
  const mine = all.filter((e) => e.kind !== 'holiday');
  const holidays = all.filter((e) => e.kind === 'holiday');
  const days: string[] = [];
  for (let d = first; d <= last; d = addDays(d, 1)) days.push(d);
  const pick = (d: string) => {
    week.select(d);
    onPick?.();
  };
  const from = week.today > first ? week.today : first;
  const coming = [
    ...(lessons.data ?? [])
      .filter((l) => l.isTest && l.status !== 'cancelled' && l.date >= from && l.date <= last)
      .map((l) => ({
        key: l.id,
        date: l.date,
        title: l.subject,
        kind: 'test' as const,
      })),
    ...mine
      .filter((e) => e.startsOn >= from && e.startsOn <= last)
      .map((e) => ({ key: e.id, date: e.startsOn, title: e.title, kind: 'event' as const })),
  ]
    .sort((x, y) => x.date.localeCompare(y.date))
    .slice(0, 6);
  return (
    <>
      <div className="flex items-center gap-2">
        <p
          className={`min-w-0 flex-1 truncate font-display font-light tracking-[-0.02em] ${big ? 'text-[26px] leading-8' : 'text-[22px] leading-7'}`}
          style={{ color: INK }}
        >
          {monthName(month)} <span style={{ color: INK_FAINT }}>{month.slice(0, 4)}</span>
        </p>
        <button
          type="button"
          aria-label={t('studentHome.shapes2.prevMonth')}
          onClick={() => setMonth((m) => monthFrom(m, -1))}
          className={ringSmall}
        >
          <CaretLeftIcon size={13} weight="bold" aria-hidden />
        </button>
        <button
          type="button"
          aria-label={t('studentHome.shapes2.nextMonth')}
          onClick={() => setMonth((m) => monthFrom(m, 1))}
          className={ringSmall}
        >
          <CaretRightIcon size={13} weight="bold" aria-hidden />
        </button>
      </div>
      <div className="mt-4 grid grid-cols-7 gap-1 text-center">
        {days.slice(0, 7).map((d) => (
          <span key={d} className="pb-1 text-[11.5px] font-medium" style={{ color: INK_FAINT }}>
            {weekdayShort(d)}
          </span>
        ))}
        {days.map((d) => {
          const on = d === week.selected;
          const isToday = d === week.today;
          const outside = d.slice(0, 7) !== month.slice(0, 7);
          const mineOn = (lessons.data ?? []).filter((l) => l.date === d);
          const test = mineOn.some((l) => l.isTest && l.status !== 'cancelled');
          const event = mine.find((e) => e.startsOn <= d && d <= e.endsOn);
          const holiday = holidays.some((e) => e.startsOn <= d && d <= e.endsOn);
          const off = mineOn.length === 0;
          return (
            <button
              key={d}
              type="button"
              aria-pressed={on}
              aria-label={dayLong(d)}
              onClick={() => pick(d)}
              className={`relative flex items-center justify-center rounded-[12px] transition-colors ${big ? 'h-11 text-[15.5px]' : 'h-10 text-[14.5px]'} ${on ? '' : 'hover:bg-[#F5F3EF]'}`}
              style={{
                ...(on
                  ? softDay(true, isToday, false)
                  : {
                      color: isToday ? TODAY_INK : INK,
                      backgroundColor: holiday ? GRAD.mint.from : undefined,
                    }),
                opacity: outside ? 0.35 : 1,
                fontWeight: isToday || on ? 600 : 400,
              }}
            >
              <span style={{ opacity: off && !on && !isToday ? 0.45 : 1 }}>
                {Number(d.slice(8))}
              </span>
              {(test || event) && (
                <span aria-hidden className="absolute bottom-1 flex gap-[3px]">
                  {test && (
                    <span
                      className="size-1 rounded-full"
                      style={{ backgroundColor: on ? '#FFFFFF' : FLAT.orange }}
                    />
                  )}
                  {event && (
                    <span
                      className="size-1 rounded-full"
                      style={{ backgroundColor: on ? '#FFFFFF' : FLAT.cobalt }}
                    />
                  )}
                </span>
              )}
            </button>
          );
        })}
      </div>
      <p
        className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px]"
        style={{ color: INK_SOFT }}
      >
        <span className="flex items-center gap-1.5">
          <span className="size-1.5 rounded-full" style={{ backgroundColor: FLAT.orange }} />
          {t('studentHome.shapes2.legendTest')}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="size-1.5 rounded-full" style={{ backgroundColor: FLAT.cobalt }} />
          {t('studentHome.shapes2.legendEvent')}
        </span>
        {holidays.length > 0 && (
          <span className="flex items-center gap-1.5">
            <span className="size-2.5 rounded-[3px]" style={{ backgroundColor: GRAD.mint.to }} />
            {t('studentHome.shapes2.legendHoliday')}
          </span>
        )}
      </p>
      {ahead && (
        <div className="mt-5 border-t pt-4" style={{ borderColor: LINE }}>
          <p className="mb-2 text-[13px] font-medium" style={{ color: INK }}>
            {t('studentHome.shapes2.ahead')}
          </p>
          {coming.length === 0 ? (
            <p className="text-[13px]" style={{ color: INK_SOFT }}>
              {t('studentHome.shapes2.aheadNone')}
            </p>
          ) : (
            <ul className="flex flex-col gap-1">
              {coming.map((c) => (
                <li key={c.key}>
                  <button
                    type="button"
                    onClick={() => pick(c.date)}
                    className="flex w-full items-center gap-3 rounded-[14px] py-1.5 pr-2 pl-1.5 text-left transition-colors hover:bg-[#F5F3EF]"
                  >
                    <span
                      className="flex size-10 shrink-0 flex-col items-center justify-center rounded-[12px] leading-none"
                      style={{ backgroundColor: PAPER, color: INK }}
                    >
                      <span className="text-[10px] font-medium" style={{ color: INK_SOFT }}>
                        {weekdayShort(c.date)}
                      </span>
                      <span className="mt-0.5 text-[15px] font-medium">
                        {Number(c.date.slice(8))}
                      </span>
                    </span>
                    <span className="min-w-0 flex-1 truncate text-[14px]" style={{ color: INK }}>
                      {c.title}
                    </span>
                    <span
                      className="shrink-0 rounded-[7px] px-2 py-1 text-[11.5px] leading-none font-medium"
                      style={
                        c.kind === 'test'
                          ? { backgroundImage: grad('coral'), color: GRAD.coral.ink }
                          : { backgroundImage: grad('cobalt'), color: GRAD.cobalt.ink }
                      }
                    >
                      {c.kind === 'test'
                        ? t('studentHome.shapes2.legendTest')
                        : t('studentHome.shapes2.legendEvent')}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </>
  );
}

/** The month in a popover under the calendar button. */
function MonthPicker({
  ctx,
  week,
  onPick,
}: {
  ctx: StudentContext;
  week: Week;
  onPick: () => void;
}) {
  return (
    <div
      className="absolute top-full right-0 z-40 mt-2 w-[316px] max-w-[calc(100vw-48px)] rounded-[28px] bg-white p-5"
      style={{ boxShadow: 'inset 0 0 0 1px #E4E0D7, 0 24px 48px -28px rgb(31 26 53 / 0.45)' }}
    >
      <MonthGrid ctx={ctx} week={week} onPick={onPick} />
    </div>
  );
}

/** The ringed calendar button and its month. */
function CalendarButton({ ctx, week }: { ctx: StudentContext; week: Week }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useDismiss(open, () => setOpen(false));
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-expanded={open}
        aria-label={t('studentHome.shapes2.calendar')}
        title={t('studentHome.shapes2.calendar')}
        onClick={() => setOpen((o) => !o)}
        className={ringButton}
        style={open ? { backgroundColor: DARK, color: '#FFFFFF' } : undefined}
      >
        <CalendarDotsIcon size={16} weight="bold" aria-hidden />
      </button>
      {open && <MonthPicker ctx={ctx} week={week} onPick={() => setOpen(false)} />}
    </div>
  );
}

/** A fact of the lesson's card: its small name over it, a hairline under it. */
function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <p className="text-[12px] leading-4 font-medium" style={{ color: INK_FAINT }}>
        {label}
      </p>
      <div className="mt-1 text-[15px] leading-[21px]" style={{ color: INK }}>
        {children}
      </div>
    </div>
  );
}

/** Handed in or not: a soft mark in green or coral. */
function HandIn({ done, late }: { done: boolean; late: boolean }) {
  const { t } = useTranslation();
  const tone: Grad = done ? 'mint' : late ? 'coral' : 'apricot';
  return (
    <span
      className="shrink-0 rounded-[7px] px-2 py-1 text-[11.5px] leading-none font-medium"
      style={{ backgroundImage: grad(tone), color: GRAD[tone].ink }}
    >
      {done ? t('studentHome.day.handed') : t('studentHome.day.notHanded')}
    </span>
  );
}

/**
 * The card of a lesson in «Форми 2» (his «оформить более прикольно, под новую стилистику», 28.10): the
 * top in the subject's gradient with its picture big and quiet behind, the subject big and light, the
 * time; the facts as small names over the words; the buttons pills with a ringed ↗.
 */
export function ShapesLessonCard({
  ctx,
  lesson,
  anchor,
  onClose,
  family = false,
  inSchedule = false,
}: {
  ctx: StudentContext;
  lesson: LessonItem;
  anchor: HTMLElement;
  onClose: () => void;
  /** a parent looks at the child's lesson: no way into it */
  family?: boolean;
  /** opened on «Розклад» itself: the lesson's pack instead of the way to the week */
  inSchedule?: boolean;
}) {
  const { t } = useTranslation();
  const { ref, pos, today, handed, due, given, join, board, who } = useLessonCard(
    ctx,
    lesson,
    anchor,
    onClose,
  );
  const look = subjectLook(lesson.subject);
  const cancelled = lesson.status === 'cancelled';
  const tone: Grad = cancelled ? 'stone' : SUBJECT_GRAD[look.tone];
  const where =
    lesson.format === 'online'
      ? t('studentHome.day.onlineLesson')
      : lesson.room
        ? capitalize(roomOf(t, lesson.room))
        : undefined;
  const facts = [
    lesson.topic && (
      <Fact key="topic" label={t('studentHome.day.topic')}>
        {lesson.topic}
      </Fact>
    ),
    lesson.bring && (
      <Fact key="bring" label={t('studentHome.day.bring')}>
        {plain(lesson.bring)}
      </Fact>
    ),
    ...due.map((h) => (
      <Fact key={h.id} label={t('studentHome.day.homeworkDue')}>
        <span className="flex items-start justify-between gap-2">
          <span className="min-w-0">{h.title}</span>
          {h.requiresSubmission && <HandIn done={handed.has(h.id)} late={lesson.date < today} />}
        </span>
      </Fact>
    )),
    ...given.map((h) => (
      <Fact key={h.id} label={t('studentHome.day.homeworkGiven')}>
        {h.title}
      </Fact>
    )),
  ].filter(Boolean);
  // inside the look's frame, so its fonts and inks reach the card
  const host = document.querySelector('.app-backdrop') ?? document.body;
  return createPortal(
    <div
      ref={ref}
      role="dialog"
      aria-label={lesson.subject}
      className={`fixed z-50 flex flex-col overflow-hidden rounded-[28px] bg-white ${
        pos?.phone ? 'inset-x-3 bottom-3 max-h-[85dvh] overflow-y-auto' : 'w-[380px]'
      }`}
      style={{
        ...(pos ? (pos.phone ? {} : { left: pos.left, top: pos.top }) : { visibility: 'hidden' }),
        boxShadow: 'inset 0 0 0 1px #E4E0D7, 0 28px 56px -30px rgb(31 26 53 / 0.5)',
      }}
    >
      <div
        className="relative isolate overflow-hidden p-5"
        style={{ backgroundImage: grad(tone), color: GRAD[tone].ink }}
      >
        {/* low in the corner: a picture never comes near a button (the × is at the top) */}
        <SubjectArt
          icon={look.icon}
          tone={tone}
          size={150}
          ghost
          className="pointer-events-none absolute -right-5 -bottom-12 -z-10"
        />
        <div className="flex items-start gap-3">
          <p className="min-w-0 flex-1 pt-2 text-[13px] leading-5 font-medium">
            {dayLong(lesson.date)} · {t('studentHome.day.period', { n: lesson.period })}
          </p>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className={`${ringButton} bg-white/50`}
          >
            <XIcon size={14} weight="bold" aria-hidden />
          </button>
        </div>
        <h3
          className={`mt-3 font-display text-[32px] leading-[38px] font-light tracking-[-0.035em] ${cancelled ? 'line-through' : ''}`}
        >
          {lesson.subject}
        </h3>
        <p
          className="mt-1 font-display text-[20px] leading-7 font-light tracking-[-0.01em]"
          style={{ color: INK }}
        >
          {timeOf(lesson.startsAt)} – {timeOf(lesson.endsAt)}
        </p>
        {(cancelled || lesson.isTest || lesson.substitute || lesson.format !== 'in_person') && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {cancelled ? (
              <Chip soft>{t('studentHome.cancelled')}</Chip>
            ) : (
              <>
                {lesson.isTest && (
                  <Chip soft tone="orange">
                    {t('studentHome.test')}
                  </Chip>
                )}
                {lesson.substitute && <Chip soft>{t('studentHome.substitute')}</Chip>}
                {lesson.format === 'online' && <Chip soft>{t('studentHome.online')}</Chip>}
                {lesson.format === 'shelter' && <Chip soft>{t('studentHome.shelter')}</Chip>}
              </>
            )}
          </div>
        )}
      </div>
      <div className="flex flex-col gap-4 px-5 pt-5">
        {(where || who) && (
          <div className="grid grid-cols-2 gap-4">
            {where && <Fact label={t('studentHome.shapes2.where')}>{where}</Fact>}
            {who && (
              <Fact
                label={
                  lesson.substitute
                    ? t('studentHome.day.substituteTeacher')
                    : t('studentHome.shapes2.teacher')
                }
              >
                {fullName(who)}
              </Fact>
            )}
          </div>
        )}
        {facts}
      </div>
      <div className="flex flex-wrap gap-2 p-5">
        {join && !family && (
          <Link
            to="/lesson/$lessonId"
            params={{ lessonId: lesson.id }}
            className={pillClass('dark')}
          >
            {t('student.lessons.joinLesson')}
            <PillIcon icon={ArrowUpRightIcon} />
          </Link>
        )}
        {board && !family && (
          <Link
            to="/class-board/$lessonId"
            params={{ lessonId: lesson.id }}
            className={pillClass(join ? 'light' : 'dark')}
          >
            {t('classBoard.open')}
            <PillIcon icon={ArrowUpRightIcon} />
          </Link>
        )}
        {inSchedule ? (
          <Link
            to="/lesson-pack/$lessonId"
            params={{ lessonId: lesson.id }}
            className={pillClass('light')}
          >
            {t('lessonPack.open')}
            <PillIcon icon={ArrowUpRightIcon} />
          </Link>
        ) : (
          <Link to="/schedule" className={pillClass('light')}>
            {t('studentHome.day.weekButton')}
            <PillIcon icon={ArrowUpRightIcon} />
          </Link>
        )}
      </div>
    </div>,
    host,
  );
}

export function ShapesDay({
  ctx,
  className = '',
  soft = false,
  week: shared,
  home,
  inPage = false,
}: {
  ctx: StudentContext;
  className?: string;
  /** «Форми 2»: gradients instead of flat colour, no shapes */
  soft?: boolean;
  /** the week chosen outside (the page «Розклад» shares it with its week) */
  week?: Week;
  /** a student abroad: the times by the family's clock */
  home?: AbroadHome | null;
  /** on the page «Розклад» itself: the day for a title, no ↗ into it */
  inPage?: boolean;
}) {
  const { t } = useTranslation();
  const now = useMinute();
  const own = useWeek(ctx);
  const week = shared ?? own;
  const clubs = useClubs(ctx.schoolId);
  const memberships = useClubMemberships([ctx.child.memberId]);
  const [open, setOpen] = useState<{ lesson: LessonItem; el: HTMLElement }>();
  const [unfolded, setUnfolded] = useState(false);
  const gradesOf = useLessonGrades(ctx, week.selected);
  const isToday = week.selected === week.today;
  const status = dayNow(week.lessons, now);
  const held = status.held;
  const slots = slotsOf(week.lessons);
  const holiday = week.events.find((e) => e.kind === 'holiday');
  const dayEvents = week.events.filter((e) => e.kind !== 'holiday');
  const dayClubs =
    held.length > 0
      ? clubsOnDay(
          clubs.data ?? [],
          memberships.data ?? [],
          ctx.child.memberId,
          isoWeekday(week.selected),
        )
      : [];
  const nextDay = week.anchor !== week.today && week.selected === week.anchor;
  const clock = timeOf(new Date(now).toISOString());

  // on a phone: from what goes on now three lessons on, the rest folded
  const focus = isToday
    ? Math.max(
        0,
        slots.findIndex(
          (s) =>
            (s.start <= now && now < s.end) || (s.kind === 'lesson' && s.lesson === status.next),
        ),
      )
    : 0;
  const lessonIndexes = slots
    .map((s, i) => (s.kind === 'lesson' ? i : -1))
    .filter((i) => i >= 0 && i >= focus);
  const lastShown = lessonIndexes[2] ?? slots.length - 1;
  const folded = (i: number) => !unfolded && (i < focus || i > lastShown);
  const foldedCount = slots.filter((s, i) => s.kind === 'lesson' && folded(i)).length;
  const anchorLabel =
    week.anchor === week.today
      ? t('studentHome.day.today')
      : week.anchor === addDays(week.today, 1)
        ? t('studentHome.day.tomorrow')
        : capitalize(weekdayShort(week.anchor));

  return (
    <section className={`flex min-w-0 flex-col rounded-[28px] bg-white p-5 md:p-6 ${className}`}>
      <Head
        title={inPage ? dayLong(week.selected) : t('studentHome.glass.week.title')}
        note={
          inPage
            ? held[0]
              ? t('studentHome.glass.week.summary', {
                  lessons: t('student.lessonsCount', { count: held.length }),
                  from: timeOf(held[0].startsAt, home?.timezone),
                  to: timeOf((held.at(-1) as LessonItem).endsAt, home?.timezone),
                })
              : undefined
            : monthName(week.selected).toLowerCase()
        }
      >
        {week.selected !== week.anchor && (
          <button
            type="button"
            onClick={week.back}
            className="inline-flex h-9 items-center rounded-full px-3.5 text-[13px] font-medium"
            style={{ backgroundColor: soft ? DARK : FLAT.lime, color: soft ? '#FFFFFF' : INK }}
          >
            {anchorLabel}
          </button>
        )}
        {inPage ? null : soft ? (
          <CalendarButton ctx={ctx} week={week} />
        ) : (
          <>
            <button
              type="button"
              aria-label={t('studentHome.glass.week.prev')}
              onClick={() => week.shift(-1)}
              className={roundButton}
              style={{ color: INK }}
            >
              <CaretLeftIcon size={14} weight="bold" aria-hidden />
            </button>
            <button
              type="button"
              aria-label={t('studentHome.glass.week.next')}
              onClick={() => week.shift(1)}
              className={roundButton}
              style={{ color: INK }}
            >
              <CaretRightIcon size={14} weight="bold" aria-hidden />
            </button>
          </>
        )}
        {!inPage && (
          <OpenArrow to="/schedule" label={t('studentHome.glass.week.all')} soft={soft} />
        )}
      </Head>

      {!inPage && <Days week={week} soft={soft} />}

      <p
        className={`mt-4 mb-3 truncate text-[13px] ${inPage ? 'hidden' : ''}`}
        style={{ color: INK_SOFT }}
      >
        <span className="font-medium" style={{ color: INK }}>
          {dayLong(week.selected)}
        </span>
        {held[0] &&
          ` · ${t('studentHome.glass.week.summary', {
            lessons: t('student.lessonsCount', { count: held.length }),
            from: timeOf(held[0].startsAt),
            to: timeOf((held.at(-1) as LessonItem).endsAt),
          })}`}
      </p>

      {week.pending ? (
        <div className="flex flex-col gap-2" aria-busy>
          {[0, 1, 2, 3, 4].map((i) => (
            <span
              key={i}
              className="h-[60px] animate-pulse rounded-[20px]"
              style={{ backgroundColor: PAPER }}
            />
          ))}
        </div>
      ) : week.lessons.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 py-10 text-center">
          {soft ? (
            <span
              className="flex size-14 items-center justify-center rounded-full"
              style={{ backgroundImage: grad('mint'), color: GRAD.mint.ink }}
            >
              <SunHorizonIcon size={26} weight="fill" aria-hidden />
            </span>
          ) : (
            <Shape kind="flower" size={56} color={FLAT.mint} />
          )}
          <p
            className="font-display text-[26px] leading-8 font-light tracking-[-0.02em]"
            style={{ color: INK }}
          >
            {holiday?.title ?? t('studentHome.glass.week.weekend')}
          </p>
          <p className="text-[13px]" style={{ color: INK_SOFT }}>
            {t('studentHome.glass.week.none')}
          </p>
          {week.selected !== week.anchor && (
            <button
              type="button"
              onClick={week.back}
              className="mt-1 inline-flex h-10 items-center rounded-full px-4 text-[13.5px] font-medium text-white"
              style={{ backgroundColor: DARK }}
            >
              {t('studentHome.glass.week.nextDay', { day: dayLong(week.anchor) })}
            </button>
          )}
        </div>
      ) : (
        <>
          {dayEvents.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-1.5">
              {dayEvents.map((e) => {
                const Icon = EVENT_LOOK[e.kind].icon;
                return (
                  <Link
                    key={e.id}
                    to="/events"
                    className="inline-flex h-9 max-w-full items-center gap-2 rounded-full px-3.5 text-[13px] font-medium"
                    style={
                      soft
                        ? {
                            backgroundImage: grad(EVENT_GRAD[e.kind]),
                            color: GRAD[EVENT_GRAD[e.kind]].ink,
                          }
                        : {
                            backgroundColor: FLAT[EVENT_FLAT[e.kind]],
                            color: e.kind === 'trip' ? '#FFFFFF' : INK,
                          }
                    }
                  >
                    <Icon size={15} weight="fill" aria-hidden className="shrink-0" />
                    <span className="truncate">
                      {e.title}
                      {e.atTime && ` · ${e.atTime}`}
                    </span>
                  </Link>
                );
              })}
            </div>
          )}
          <ol className="flex flex-col">
            {isToday && status.before && status.first && (
              <li>
                <Now time={clock} soft={soft}>
                  {t('studentHome.glass.week.nowBefore', {
                    min: minutesBetween(now, Date.parse(status.first.startsAt)),
                  })}
                </Now>
              </li>
            )}
            {slots.map((slot, index) => {
              const hidden = folded(index) ? 'max-md:hidden' : '';
              if (slot.kind === 'break') {
                const inside = isToday && slot.start <= now && now < slot.end;
                return (
                  <li key={`break-${slot.start}`} className={hidden}>
                    {inside ? (
                      <Now time={clock} soft={soft}>
                        {t('studentHome.glass.week.nowBreak', {
                          min: Math.max(1, Math.ceil((slot.end - now) / 60_000)),
                        })}
                      </Now>
                    ) : (
                      <Gap slot={slot} />
                    )}
                  </li>
                );
              }
              const l = slot.lesson;
              const prev = slots[index - 1];
              const state =
                l.status === 'cancelled'
                  ? 'ahead'
                  : isToday && l === status.live
                    ? 'live'
                    : isToday && slot.end <= now
                      ? 'done'
                      : (isToday && l === status.next) || (!isToday && l === held[0] && nextDay)
                        ? 'next'
                        : 'ahead';
              return (
                <li key={l.id} className={`${prev?.kind === 'lesson' ? 'mt-1.5' : ''} ${hidden}`}>
                  <LessonRow
                    lesson={l}
                    grades={gradesOf(l)}
                    state={state}
                    now={now}
                    isToday={isToday}
                    soft={soft}
                    home={home}
                    onOpen={(lesson, el) => setOpen({ lesson, el })}
                  />
                </li>
              );
            })}
            {isToday && status.over && status.last && (
              <li className="mt-1">
                <Now time={clock} soft={soft}>
                  {t('studentHome.glass.week.nowOver', { time: timeOf(status.last.endsAt) })}
                </Now>
              </li>
            )}
          </ol>
          {foldedCount > 0 &&
            (soft ? (
              // «Форми 2» (his «не прикольно, когда карточка так раскрывается», 28.10): the rest of the
              // day is one more row into the schedule, as in the cards of what to do
              <Link
                to="/schedule"
                className="group mt-1.5 flex h-11 items-center gap-3 rounded-[20px] px-4 text-[13.5px] font-medium transition-colors hover:bg-[#EFECE6] md:hidden"
                style={{ backgroundColor: PAPER, color: INK }}
              >
                <span className="min-w-0 flex-1 truncate">
                  {t('studentHome.day.more', { count: foldedCount })}
                </span>
                <ArrowUpRightIcon
                  size={13}
                  weight="bold"
                  aria-hidden
                  className="shrink-0 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </Link>
            ) : (
              <button
                type="button"
                onClick={() => setUnfolded(true)}
                className="mt-2 self-start rounded-full px-3.5 py-1.5 text-[13px] font-medium md:hidden"
                style={{ backgroundColor: PAPER, color: INK }}
              >
                {t('studentHome.day.more', { count: foldedCount })}
              </button>
            ))}
          {dayClubs.length > 0 && (
            <div className="mt-2 flex flex-col gap-1.5">
              {dayClubs.map(({ club, slot }) => (
                <Link
                  key={`${club.id}-${slot.startsAt}`}
                  to="/clubs"
                  className={`relative isolate flex min-h-[60px] items-center gap-3 overflow-hidden rounded-[20px] py-2.5 pr-4 ring-1 ring-[#E4E0D7] ring-inset transition-colors hover:bg-[#FAF9F6] ${soft ? 'pl-4' : 'pl-3'}`}
                >
                  {soft ? (
                    <EventArt
                      kind="other"
                      tone="lime"
                      size={86}
                      ghost
                      className="pointer-events-none absolute top-1/2 right-[68px] -z-10 -translate-y-[42%]"
                    />
                  ) : (
                    <Shape kind="plus" size={30} color={FLAT.lime} />
                  )}
                  <span className="min-w-0 flex-1">
                    <span
                      className="block truncate text-[16px] leading-[22px] font-medium"
                      style={{ color: INK }}
                    >
                      {club.title}
                    </span>
                    <span
                      className="block truncate text-[12.5px] leading-[18px]"
                      style={{ color: INK_SOFT }}
                    >
                      {[t('studentHome.glass.week.club'), club.room].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                  <span
                    className="flex shrink-0 flex-col items-end leading-tight"
                    style={{ color: INK }}
                  >
                    <span className="font-display text-[17px] font-light">
                      {slot.startsAt.slice(0, 5)}
                    </span>
                    <span className="text-[12px]" style={{ color: INK_SOFT }}>
                      {slot.endsAt.slice(0, 5)}
                    </span>
                  </span>
                </Link>
              ))}
            </div>
          )}
          {nextDay && <Pack ctx={ctx} date={week.selected} soft={soft} />}
        </>
      )}
      {open &&
        (soft ? (
          <ShapesLessonCard
            ctx={ctx}
            lesson={open.lesson}
            anchor={open.el}
            onClose={() => setOpen(undefined)}
          />
        ) : (
          <LessonCard
            ctx={ctx}
            lesson={open.lesson}
            anchor={open.el}
            onClose={() => setOpen(undefined)}
          />
        ))}
    </section>
  );
}
